import React, { useEffect, useState } from 'react';
import { CreditCard, X, AlertCircle, Loader, CheckCircle } from 'lucide-react';
import paymentService from '../../services/paymentService';
import { formatDoctorName } from '../../utils/doctorName';
import usePricing from '../../hooks/usePricing';
import { formatCOP } from '../../config/pricing';

function PaymentModal({ appointment, onClose, onSuccess }) {
  const [state, setState] = useState('idle');
  const [error, setError] = useState(null);
  const [paymentData, setPaymentData] = useState(null);
  const [paymentResult, setPaymentResult] = useState(null);
  const { pricing } = usePricing();

  useEffect(() => {
    const initPayment = async () => {
      try {
        if (!appointment?._id) {
          setError('No hay cita seleccionada');
          setState('error');
          return;
        }

        setState('loading');
        const data = await paymentService.initializePayment(appointment._id);
        setPaymentData(data);
        setState('ready');
      } catch (err) {
        console.error('❌ Error inicializando pago:', err);
        setError(err.message || 'Error al inicializar el pago');
        setState('error');
      }
    };

    initPayment();
  }, [appointment?._id]);

  const displayAmount = Number(paymentData?.amount || 0) / 100;
  const formattedAmount = displayAmount.toLocaleString('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  // El monto lo fija el backend; si supera el valor base, la diferencia es el
  // recargo nocturno o dominical del horario de la cita.
  const surchargeIncluded =
    pricing && displayAmount > pricing.basePrice ? displayAmount - pricing.basePrice : 0;

  useEffect(() => {
    if (state !== 'ready' || !paymentData) return;

    const loadWompiWidget = async () => {
      try {
        const script = document.createElement('script');
        script.src = 'https://checkout.wompi.co/widget.js';
        script.async = true;
        script.setAttribute('data-render', 'button');
        script.setAttribute('data-public-key', paymentData.publicKey);
        script.setAttribute('data-currency', 'COP');
        script.setAttribute('data-amount-in-cents', String(paymentData.amount));
        script.setAttribute('data-reference', paymentData.id);

        if (paymentData.integritySignature) {
          script.setAttribute('data-signature:integrity', paymentData.integritySignature);
        }

        if (paymentData.acceptanceToken) {
          script.setAttribute('data-acceptance-token', paymentData.acceptanceToken);
        }

        script.setAttribute(
          'data-redirect-url',
          window.location.origin + '/dashboard/patient'
        );

        const container = document.getElementById('wompi-widget-container');
        if (container) {
          container.innerHTML = '';
          container.appendChild(script);
        }
      } catch (err) {
        console.error('❌ Error cargando widget:', err);
        setError('Error cargando el widget de pago');
        setState('error');
      }
    };

    loadWompiWidget();
  }, [state, paymentData]);

  useEffect(() => {
    if (state !== 'ready' || !appointment?._id) return;

    const startedAt = Date.now();
    const timeoutMs = 2 * 60 * 1000;

    const pollInterval = setInterval(async () => {
      try {
        if (Date.now() - startedAt > timeoutMs) {
          setPaymentResult({
            success: false,
            message: 'No se recibió confirmación del pago. Puedes intentar nuevamente.'
          });
          setError('No se recibió confirmación del pago');
          setState('error');
          clearInterval(pollInterval);
          return;
        }

        const status = await paymentService.getPaymentStatus(appointment._id);
        console.log('📊 Estado del pago:', status.status);

        if (status.status === 'completed') {
          setPaymentResult({
            success: true,
            message: '¡Pago exitoso!',
            transactionId: status.transactionId || status.reference
          });
          setState('success');
          clearInterval(pollInterval);

          setTimeout(() => {
            onSuccess?.();
            onClose();
          }, 2000);
        } else if (status.status === 'failed') {
          setPaymentResult({
            success: false,
            message: 'El pago fue rechazado. Intenta de nuevo.'
          });
          setError('El pago fue rechazado');
          setState('error');
          clearInterval(pollInterval);
        } else if (status.status === 'no_transaction') {
          setPaymentResult({
            success: false,
            message: 'No se registró ningún pago. Si cerraste el widget, puedes intentarlo nuevamente.'
          });
          setError('No se registró ningún pago');
          setState('error');
          clearInterval(pollInterval);
        }
      } catch (err) {
        console.warn('⚠️ Error en polling:', err.message);
      }
    }, 2000);

    return () => clearInterval(pollInterval);
  }, [state, appointment?._id, onClose, onSuccess]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 flex items-center justify-between sticky top-0">
          <div className="flex items-center gap-3">
            <CreditCard className="h-6 w-6" />
            <h2 className="text-2xl font-bold">Pagar Consulta</h2>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-blue-800 p-2 rounded-full transition"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {state === 'loading' && (
            <div className="flex flex-col items-center justify-center py-12 gap-4">
              <Loader className="h-12 w-12 text-blue-600 animate-spin" />
              <p className="text-gray-600 font-semibold">Preparando el pago...</p>
              <p className="text-gray-500 text-sm">Por favor espera</p>
            </div>
          )}

          {state === 'ready' && paymentData && (
            <>
              <div className="bg-gray-50 p-4 rounded-lg space-y-3 border border-gray-200">
                <div>
                  <p className="text-sm text-gray-600">Doctor</p>
                  <p className="font-semibold text-gray-800">
                    {formatDoctorName(appointment.doctor)}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-600">Especialidad</p>
                  <p className="font-semibold text-gray-800">
                    {appointment.doctor?.specialization}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-600">Costo de la consulta</p>
                  <p className="font-bold text-2xl text-blue-600">
                    {formattedAmount}
                  </p>
                  {surchargeIncluded > 0 && (
                    <p className="text-xs text-amber-700 mt-1">
                      Incluye un recargo de {formatCOP(surchargeIncluded)} COP por ser una consulta
                      en horario nocturno o en domingo.
                    </p>
                  )}
                </div>
              </div>

              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 bg-gray-50">
                <div id="wompi-widget-container" className="flex justify-center">
                  <div className="flex items-center gap-2 text-gray-500">
                    <Loader className="h-4 w-4 animate-spin" />
                    <span className="text-sm">Cargando opciones de pago...</span>
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-2 text-xs text-gray-600 bg-blue-50 p-3 rounded-lg border border-blue-200">
                <CheckCircle className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                <p>Tu pago es seguro con Wompi. Aceptamos todas las tarjetas de crédito y débito.</p>
              </div>
            </>
          )}

          {state === 'error' && (
            <div className="flex flex-col items-center justify-center py-12 gap-4">
              <AlertCircle className="h-12 w-12 text-red-600" />
              <div className="text-center">
                <p className="text-gray-800 font-semibold">Error en el pago</p>
                <p className="text-gray-600 text-sm mt-2">{paymentResult?.message || error}</p>
              </div>
              <button
                onClick={onClose}
                className="mt-4 px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-semibold"
              >
                Cerrar
              </button>
            </div>
          )}

          {state === 'success' && (
            <div className="flex flex-col items-center justify-center py-12 gap-4">
              <div className="bg-green-100 p-4 rounded-full">
                <CheckCircle className="h-12 w-12 text-green-600" />
              </div>
              <div className="text-center">
                <p className="text-gray-800 font-bold text-lg">¡Pago Exitoso!</p>
                <p className="text-gray-600 text-sm mt-2">Tu cita ha sido confirmada y pagada</p>
                {paymentResult?.transactionId && (
                  <p className="text-gray-500 text-xs mt-3">
                    Ref: {paymentResult.transactionId}
                  </p>
                )}
              </div>
              <div className="w-full bg-blue-50 text-blue-700 p-3 rounded-lg text-sm text-center">
                Cerrando en 2 segundos...
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PaymentModal;